import type { CSSProperties } from 'react';

import {
  getReadableTextColor,
  type ListStyle,
  type StylizedListTitleLevel,
  type ThemeTokens,
} from '@course-authoring/shared-types';

import { getTitleTypography } from './titleTypography';

export interface StylizedListStyles {
  container: CSSProperties;
  title: CSSProperties;
  list: CSSProperties;
  item: CSSProperties;
  numberMarker: CSSProperties;
  bulletMarker: CSSProperties;
  itemContent: CSSProperties;
  placeholder: CSSProperties;
}

function adjustPixelValue(value: string, delta: number): string {
  const numericValue = Number.parseFloat(value);

  if (Number.isNaN(numericValue)) {
    return value;
  }

  return `${numericValue + delta}px`;
}

/**
 * Component-layout values for the Stylized List. Inlined as part of the Phase 4
 * component-token flatten — these are bespoke layout values (widths, badge sizes,
 * marker color) not part of the design-system token set.
 */
const STYLIZED_LIST = {
  // NA-205: lists widen to 90% and bullet dots shrink to 10px on narrow phones (≤576px).
  width: { desktop: '60%', mobile: '80%', smallMobile: '90%' },
  itemGap: { mobile: '20px' },          // desktop derived from spacing scale
  contentGap: { desktop: '20px', mobile: '20px' },
  numberedBadgeSize: { desktop: '36px', mobile: '30px' },
  bulletBadgeSize: { desktop: '16px', mobile: '16px', smallMobile: '10px' },
  numberFontSize: { desktop: '20px', mobile: '18px' },
  markerColor: '#222442',
} as const;

function getStylizedListTitleGap(tokens: ThemeTokens, isMobile: boolean): string {
  const mobileTitleGap = tokens.spacing.xl ?? '24px';
  const desktopTitleGapBase = tokens.spacing["2xl"] ?? '32px';

  if (isMobile) {
    return mobileTitleGap;
  }

  return adjustPixelValue(desktopTitleGapBase, -2);
}

function getStylizedListItemGap(
  tokens: ThemeTokens,
  isMobile: boolean,
  listStyle: ListStyle
): string {
  const desktopItemGapBase = tokens.spacing["2xl"] ?? '32px';

  if (isMobile) {
    return STYLIZED_LIST.itemGap.mobile;
  }

  return listStyle === 'bulleted'
    ? adjustPixelValue(desktopItemGapBase, -2)
    : adjustPixelValue(desktopItemGapBase, 3);
}

export function createStylizedListStyles(
  tokens: ThemeTokens,
  isMobile: boolean,
  isSmallMobile: boolean,
  titleLevel: StylizedListTitleLevel = 'h3',
  listStyle: ListStyle = 'numbered',
  markerColor?: string
): StylizedListStyles {
  const resolvedMarkerColor = markerColor ?? STYLIZED_LIST.markerColor;
  const width = isSmallMobile
    ? STYLIZED_LIST.width.smallMobile
    : isMobile
      ? STYLIZED_LIST.width.mobile
      : STYLIZED_LIST.width.desktop;
  const titleGap = getStylizedListTitleGap(tokens, isMobile);
  const itemGap = getStylizedListItemGap(tokens, isMobile, listStyle);
  const contentGap = isMobile ? STYLIZED_LIST.contentGap.mobile : STYLIZED_LIST.contentGap.desktop;
  const numberedBadgeSize = isMobile
    ? STYLIZED_LIST.numberedBadgeSize.mobile
    : STYLIZED_LIST.numberedBadgeSize.desktop;
  const bulletBadgeSize = isSmallMobile
    ? STYLIZED_LIST.bulletBadgeSize.smallMobile
    : isMobile
      ? STYLIZED_LIST.bulletBadgeSize.mobile
      : STYLIZED_LIST.bulletBadgeSize.desktop;
  const numberFontSize = isMobile
    ? STYLIZED_LIST.numberFontSize.mobile
    : STYLIZED_LIST.numberFontSize.desktop;
  const numberMarkerMarginTop = '-2px';
  const bulletMarkerMarginTop = adjustPixelValue(tokens.spacing.sm ?? '8px', -1);
  // NA-205: list item text is reading body copy — hold 20px through the tablet mid-range and
  // drop to 16px only on narrow phones (≤576px), matching the other body-copy blocks.
  const bodyFont = isSmallMobile ? tokens.typography.mobile.body : tokens.typography.body;
  const titleFont = getTitleTypography(tokens, titleLevel, { isMobile });

  return {
    container: {
      width,
      maxWidth: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: titleGap,
      flexShrink: 0,
      alignSelf: 'center',
      margin: '0 auto',
      boxSizing: 'border-box',
    },
    title: {
      width: '100%',
      margin: 0,
      fontFamily: tokens.typography.fontFamily,
      fontSize: titleFont.size,
      fontStyle: 'normal',
      fontWeight: titleFont.weight,
      lineHeight: titleFont.lineHeight,
      color: tokens.text.primary,
    },
    list: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: itemGap,
      margin: 0,
      padding: 0,
      listStyle: 'none',
      boxSizing: 'border-box',
    },
    item: {
      width: '100%',
      minWidth: 0,
      display: 'flex',
      alignItems: 'flex-start',
      gap: contentGap,
      boxSizing: 'border-box',
    },
    numberMarker: {
      width: numberedBadgeSize,
      minWidth: numberedBadgeSize,
      height: numberedBadgeSize,
      borderRadius: '50%',
      backgroundColor: resolvedMarkerColor,
      color: getReadableTextColor(resolvedMarkerColor),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      fontFamily: tokens.typography.fontFamily,
      fontSize: numberFontSize,
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: 'normal',
      flexShrink: 0,
      marginTop: numberMarkerMarginTop,
    },
    bulletMarker: {
      width: bulletBadgeSize,
      minWidth: bulletBadgeSize,
      height: bulletBadgeSize,
      borderRadius: '50%',
      backgroundColor: resolvedMarkerColor,
      flexShrink: 0,
      marginTop: bulletMarkerMarginTop,
    },
    itemContent: {
      flex: 1,
      minWidth: 0,
      fontFamily: tokens.typography.fontFamily,
      fontSize: bodyFont.size,
      fontStyle: 'normal',
      fontWeight: bodyFont.weight,
      lineHeight: bodyFont.lineHeight,
      color: tokens.text.primary,
    },
    placeholder: {
      margin: 0,
      fontFamily: tokens.typography.fontFamily,
      fontSize: bodyFont.size,
      fontStyle: 'italic',
      fontWeight: bodyFont.weight,
      lineHeight: bodyFont.lineHeight,
      color: tokens.text.muted,
    },
  };
}
